import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  TimePicker,
  Typography
} from "antd";
import {
  CalendarOutlined,
  FileTextOutlined,
  OrderedListOutlined,
  PlusOutlined,
  SettingOutlined,
  ThunderboltOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { formatDay, formatEuro } from "../workReports/format";
import {
  createVisit,
  getMeta,
  getOverview,
  invoiceVisits,
  listContracts,
  listInstallations,
  listVisits,
  planNow,
  saveSettings
} from "./api";
import {
  CONTRACT_STATUS_COLORS,
  DUE_COLORS,
  VISIT_STATUS_COLORS,
  billingLabels,
  contractStatusLabels,
  dueLabels,
  typeLabels,
  visitStatusLabels
} from "./labels";
import ContractDrawer from "./ContractDrawer";
import InstallationDrawer from "./InstallationDrawer";
import TemplatesModal from "./TemplatesModal";
import "./maintenance.css";

const TABS = ["due", "visits", "contracts", "installations"];

const SettingsModal = ({ open, settings, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && settings) {
      form.setFieldsValue({ ...settings, slotTime: dayjs(settings.slotTime, "HH:mm") });
    }
  }, [open, settings]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await saveSettings({ ...values, slotTime: values.slotTime.format("HH:mm") });
      Success(t("Saved"));
      onSaved(res.data.settings);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="maintenance-page"
      open={open}
      width={640}
      title={t("Maintenance settings")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={[12, 0]}>
          <Col xs={12}>
            <Form.Item
              name="leadDays"
              label={t("Plan days ahead")}
              extra={t("Visits are created this many days before they are due.")}
            >
              <InputNumber className="mnt-full" min={0} max={180} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="slotTime" label={t("Proposed start time")}>
              <TimePicker className="mnt-full" format="HH:mm" minuteStep={15} allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item name="slotCount" label={t("Proposed dates")}>
              <Select options={[1, 2, 3].map(n => ({ value: n, label: String(n) }))} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name="notifyCustomer"
              label={t("Email date proposal to the customer")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="emailCopy"
              label={t("Email the signed protocol to the customer")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="protocolTerms" label={t("Text above the signatures")}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={3000} />
        </Form.Item>
        <Form.Item name="contractScope" label={t("Standard scope of services for new contracts")}>
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} maxLength={5000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const NewVisitModal = ({ open, meta, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [installations, setInstallations] = useState([]);
  const [busy, setBusy] = useState(false);
  const client = Form.useWatch("client", form);

  useEffect(() => {
    if (open) form.setFieldsValue({ plannedDate: dayjs(), propose: false, notify: false });
  }, [open]);

  useEffect(() => {
    if (!client) return setInstallations([]);
    listInstallations({ client })
      .then(res => setInstallations(res.data.data))
      .catch(generalErrorAlert);
  }, [client]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await createVisit({
        installations: values.installations,
        plannedDate: values.plannedDate.format("YYYY-MM-DD"),
        propose: values.propose,
        notify: values.propose && values.notify
      });
      history.push(`/user/maintenance/visits/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="maintenance-page"
      open={open}
      title={t("New visit")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="client" label={t("Customer")} rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={(meta?.clients || []).map(c => ({ value: c._id, label: c.name }))}
            onChange={() => form.setFieldsValue({ installations: [] })}
          />
        </Form.Item>
        <Form.Item
          name="installations"
          label={t("Installations")}
          rules={[{ required: true }]}
          extra={t("All installations of a visit must be at the same branch.")}
        >
          <Select
            mode="multiple"
            optionFilterProp="label"
            options={installations.map(i => ({
              value: i._id,
              label: [i.name, i.location, i.branch?.name].filter(Boolean).join(" · ")
            }))}
          />
        </Form.Item>
        <Form.Item name="plannedDate" label={t("Date")} rules={[{ required: true }]}>
          <DatePicker className="mnt-full" format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        <Form.Item name="propose" valuePropName="checked">
          <Checkbox>{t("Propose dates in the calendar")}</Checkbox>
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {() =>
            form.getFieldValue("propose") && (
              <Form.Item name="notify" valuePropName="checked">
                <Checkbox>{t("Email the proposal to the customer")}</Checkbox>
              </Form.Item>
            )
          }
        </Form.Item>
      </Form>
    </Modal>
  );
};

const Maintenance = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const initialTab = new URLSearchParams(location.search).get("tab");
  const [tab, setTab] = useState(TABS.includes(initialTab) ? initialTab : "due");
  const [meta, setMeta] = useState(null);
  const [overview, setOverview] = useState(null);
  const [visits, setVisits] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [visitFilter, setVisitFilter] = useState("open");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [contractDrawer, setContractDrawer] = useState(null); // { id }
  const [installationDrawer, setInstallationDrawer] = useState(null);
  const [newVisitOpen, setNewVisitOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const types = typeLabels(t);
  const visitStatuses = visitStatusLabels(t);
  const contractStatuses = contractStatusLabels(t);
  const billings = billingLabels(t);
  const dues = dueLabels(t);

  const loadMeta = () =>
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === "due") setOverview((await getOverview()).data);
      if (tab === "visits") {
        const filter =
          visitFilter === "to_invoice"
            ? { status: "completed", billing: "open" }
            : { status: visitFilter === "all" ? undefined : visitFilter };
        const res = await listVisits({ ...filter, search: searchText });
        setVisits(res.data.data);
        setSelected(prev => prev.filter(id => res.data.data.some(v => v._id === id)));
      }
      if (tab === "contracts")
        setContracts((await listContracts({ search: searchText })).data.data);
      if (tab === "installations") {
        setInstallations(
          (await listInstallations({ search: searchText, active: "all" })).data.data
        );
      }
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
  }, []);

  useEffect(() => {
    load();
  }, [tab, searchText, visitFilter]);

  const changeTab = key => {
    setTab(key);
    setSearchText("");
    history.replace({ search: `?tab=${key}` });
  };

  const runPlanning = async () => {
    setBusy(true);
    try {
      const res = await planNow();
      Success(t("{{n}} visits planned", { n: res.data.created.length }));
      load();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const planOne = async item => {
    setBusy(true);
    try {
      const plannedDate =
        item.nextDue > dayjs().format("YYYY-MM-DD") ? item.nextDue : dayjs().format("YYYY-MM-DD");
      const res = await createVisit(
        item.kind === "contract"
          ? { contract: item._id, plannedDate, propose: true }
          : { installations: [item._id], plannedDate, propose: true }
      );
      history.push(`/user/maintenance/visits/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const invoice = async () => {
    setBusy(true);
    try {
      const res = await invoiceVisits(selected);
      Success(t("Draft invoice created. Check it and send it from Invoices."));
      history.push("/user/invoices", { highlight: res.data.invoice._id });
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const openVisit = record => history.push(`/user/maintenance/visits/${record._id}`);
  const clientCell = record => record.client?.name || "—";

  // ---- Columns ----
  const visitColumns = [
    {
      title: t("Visit"),
      key: "number",
      mobile: "title",
      render: (_, v) => (
        <div>
          <Link to={`/user/maintenance/visits/${v._id}`}>
            <Typography.Text strong>{v.number}</Typography.Text>
          </Link>{" "}
          <Typography.Text type="secondary">{formatDay(v.date || v.plannedDate)}</Typography.Text>
          <div className="mnt-small mnt-clamp">{(v.installations || []).join(", ")}</div>
        </div>
      )
    },
    { title: t("Customer"), key: "client", mobile: "subtitle", render: (_, v) => clientCell(v) },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      render: (_, v) => (
        <Space size={[4, 4]} wrap>
          <Tag color={VISIT_STATUS_COLORS[v.status]}>{visitStatuses[v.status]}</Tag>
          {v.overdue && <Tag color="error">{t("Overdue")}</Tag>}
          {v.defects && <Tag color="error">{t("Defects")}</Tag>}
          {v.status === "completed" && <Tag>{billings[v.billing]}</Tag>}
        </Space>
      )
    },
    {
      title: t("Net"),
      key: "net",
      align: "right",
      render: (_, v) => (v.net === undefined ? "" : formatEuro(v.net, lng))
    }
  ];

  const selectable = v => v.status === "completed" && v.billing === "open";
  if (isMobile && meta?.can.invoice && tab === "visits") {
    visitColumns.push({
      title: "",
      key: "actions",
      mobile: "actions",
      render: (_, v) =>
        selectable(v) ? (
          <Checkbox
            checked={selected.includes(v._id)}
            onChange={event =>
              setSelected(prev =>
                event.target.checked ? [...prev, v._id] : prev.filter(id => id !== v._id)
              )
            }
          >
            {t("Select for invoice")}
          </Checkbox>
        ) : null
    });
  }

  const dueColumns = [
    {
      title: t("Due"),
      key: "label",
      mobile: "title",
      render: (_, d) => (
        <div>
          <Typography.Text strong>{d.label}</Typography.Text>{" "}
          <Typography.Text type="secondary">{formatDay(d.nextDue)}</Typography.Text>
          <div className="mnt-small">
            {d.kind === "contract" ? t("Contract") : t("Installation")}
          </div>
        </div>
      )
    },
    { title: t("Customer"), key: "client", mobile: "subtitle", render: (_, d) => clientCell(d) },
    {
      title: t("Status"),
      key: "state",
      mobile: "badge",
      render: (_, d) => (
        <Space size={[4, 4]} wrap>
          <Tag color={DUE_COLORS[d.state]}>{dues[d.state]}</Tag>
          {d.planned && <Tag color="processing">{t("Planned")}</Tag>}
        </Space>
      )
    },
    {
      title: "",
      key: "actions",
      mobile: "actions",
      render: (_, d) =>
        !d.planned && meta?.can.create ? (
          <Button
            size="small"
            icon={<CalendarOutlined />}
            loading={busy}
            onClick={() => planOne(d)}
          >
            {t("Plan visit")}
          </Button>
        ) : null
    }
  ];

  const contractColumns = [
    {
      title: t("Contract"),
      key: "number",
      mobile: "title",
      render: (_, c) => (
        <div>
          <Typography.Text strong>{c.number}</Typography.Text>{" "}
          <Typography.Text type="secondary">{c.title}</Typography.Text>
          <div className="mnt-small">
            {t("{{n}} installations", { n: c.installationCount })} ·{" "}
            {c.billingMode === "annual_flat"
              ? t("{{amount}} per year", { amount: formatEuro(c.annualFee, lng) })
              : t("{{amount}} per visit", { amount: formatEuro(c.pricePerVisit, lng) })}
          </div>
        </div>
      )
    },
    { title: t("Customer"), key: "client", mobile: "subtitle", render: (_, c) => clientCell(c) },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      render: (_, c) => (
        <Tag color={CONTRACT_STATUS_COLORS[c.status]}>{contractStatuses[c.status]}</Tag>
      )
    },
    {
      title: t("Next due"),
      key: "nextDue",
      align: "right",
      render: (_, c) => formatDay(c.nextDue) || "—"
    }
  ];

  const installationColumns = [
    {
      title: t("Installation"),
      key: "name",
      mobile: "title",
      render: (_, i) => (
        <div>
          <Typography.Text strong>{i.name}</Typography.Text>
          <div className="mnt-small">
            {[types[i.type], [i.manufacturer, i.model].filter(Boolean).join(" "), i.location]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      )
    },
    {
      title: t("Customer"),
      key: "client",
      mobile: "subtitle",
      render: (_, i) => [i.client?.name, i.branch?.name].filter(Boolean).join(" · ") || "—"
    },
    {
      title: t("Status"),
      key: "state",
      mobile: "badge",
      render: (_, i) => (
        <Space size={[4, 4]} wrap>
          {!i.active && <Tag>{t("Retired")}</Tag>}
          {i.active && i.dueState !== "upcoming" && i.dueState !== "none" && (
            <Tag color={DUE_COLORS[i.dueState]}>{dues[i.dueState]}</Tag>
          )}
          {i.contract && <Tag color="processing">{i.contract.number}</Tag>}
        </Space>
      )
    },
    {
      title: t("Next due"),
      key: "nextDue",
      align: "right",
      render: (_, i) => formatDay(i.nextDue) || "—"
    }
  ];

  const table = (columns, dataSource, extra = {}) => {
    const node = (
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        mobileAvatar={false}
        pagination={dataSource.length > 50 ? { pageSize: 50 } : false}
        {...extra}
      />
    );
    return isMobile ? node : <Card styles={{ body: { padding: 0 } }}>{node}</Card>;
  };

  const selectedVisits = visits.filter(v => selected.includes(v._id));
  const sameCustomer =
    new Set(selectedVisits.map(v => `${v.client?._id}:${v.clientBranch || ""}`)).size === 1;

  const primaryAction = {
    due: null,
    visits: meta?.can.create && (
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewVisitOpen(true)}>
        {t("New visit")}
      </Button>
    ),
    contracts: meta?.can.create && (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setContractDrawer({ id: null })}
      >
        {t("New contract")}
      </Button>
    ),
    installations: meta?.can.create && (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setInstallationDrawer({ id: null })}
      >
        {t("New installation")}
      </Button>
    )
  }[tab];

  return (
    <div className="maintenance-page">
      <Tabs
        activeKey={tab}
        onChange={changeTab}
        items={[
          {
            key: "due",
            label: (
              <span>
                {t("Due")}
                {overview?.overdueCount > 0 && (
                  <Tag color="error" className="mnt-tab-count">
                    {overview.overdueCount}
                  </Tag>
                )}
              </span>
            )
          },
          { key: "visits", label: t("Visits") },
          { key: "contracts", label: t("Contracts") },
          { key: "installations", label: t("Installations") }
        ]}
      />

      <PageToolbar
        filters={
          tab === "due" ? null : (
            <Space direction="vertical" className="mnt-full">
              <SearchBarComp key={tab} handleSearch={setSearchText} />
              {tab === "visits" && (
                <Segmented
                  value={visitFilter}
                  onChange={value => {
                    setVisitFilter(value);
                    setSelected([]);
                  }}
                  options={[
                    { value: "open", label: t("Open") },
                    { value: "to_invoice", label: t("To invoice") },
                    { value: "completed", label: visitStatuses.completed },
                    { value: "all", label: t("All") }
                  ]}
                />
              )}
            </Space>
          )
        }
        actions={
          <Space wrap>
            {tab === "due" && meta?.can.create && (
              <Button icon={<ThunderboltOutlined />} loading={busy} onClick={runPlanning}>
                {t("Plan due visits now")}
              </Button>
            )}
            {meta?.can.update && (
              <Button icon={<OrderedListOutlined />} onClick={() => setTemplatesOpen(true)}>
                {t("Checklists")}
              </Button>
            )}
            {meta?.can.settings && (
              <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
                {t("Settings")}
              </Button>
            )}
            {primaryAction}
          </Space>
        }
      />

      {tab === "due" && (
        <>
          {overview?.overdueCount > 0 && (
            <Alert
              className="mb-3"
              type="error"
              showIcon
              message={t("{{n}} overdue inspections", { n: overview.overdueCount })}
            />
          )}
          <Typography.Title level={5}>{t("Open visits")}</Typography.Title>
          {overview && !overview.visits.length ? (
            <Card className="mb-3">
              <Empty description={t("No open visits")} />
            </Card>
          ) : (
            <div className="mb-3">
              {table(visitColumns, overview?.visits || [], {
                onRow: record => ({ onDoubleClick: () => openVisit(record) })
              })}
            </div>
          )}
          <Typography.Title level={5}>{t("Due and overdue")}</Typography.Title>
          {overview && !overview.due.length ? (
            <Card>
              <Empty
                description={
                  <>
                    <Typography.Paragraph>
                      {t("Nothing is due in the planning period.")}
                    </Typography.Paragraph>
                    <Typography.Paragraph type="secondary" className="mnt-empty-text">
                      {t(
                        "Record your customers' installations, put them under a maintenance contract, and visits are planned automatically before they fall due."
                      )}
                    </Typography.Paragraph>
                  </>
                }
              />
            </Card>
          ) : (
            table(dueColumns, overview?.due || [], { rowKey: d => `${d.kind}:${d._id}` })
          )}
        </>
      )}

      {tab === "visits" && (
        <>
          {selected.length > 0 && (
            <Alert
              className="mb-3"
              type={sameCustomer ? "success" : "warning"}
              message={
                sameCustomer
                  ? t("{{n}} visits selected", { n: selected.length })
                  : t("Select visits of one customer and branch.")
              }
              action={
                <Space>
                  <Button size="small" onClick={() => setSelected([])}>
                    {t("Clear")}
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<FileTextOutlined />}
                    disabled={!sameCustomer}
                    loading={busy}
                    onClick={invoice}
                  >
                    {t("Create invoice")}
                  </Button>
                </Space>
              }
            />
          )}
          {table(visitColumns, visits, {
            rowSelection:
              meta?.can.invoice && !isMobile
                ? {
                    selectedRowKeys: selected,
                    onChange: keys => setSelected(keys),
                    getCheckboxProps: record => ({ disabled: !selectable(record) })
                  }
                : undefined,
            onRow: record => ({ onDoubleClick: () => openVisit(record) })
          })}
        </>
      )}

      {tab === "contracts" &&
        table(contractColumns, contracts, {
          onRow: record => ({ onClick: () => setContractDrawer({ id: record._id }) }),
          rowClassName: () => "mnt-clickable"
        })}

      {tab === "installations" &&
        table(installationColumns, installations, {
          onRow: record => ({ onClick: () => setInstallationDrawer({ id: record._id }) }),
          rowClassName: () => "mnt-clickable"
        })}

      <ContractDrawer
        open={Boolean(contractDrawer)}
        id={contractDrawer?.id || null}
        meta={meta}
        onClose={() => setContractDrawer(null)}
        onSaved={(data, created) => {
          if (created && data) setContractDrawer({ id: data._id });
          load();
        }}
      />
      <InstallationDrawer
        open={Boolean(installationDrawer)}
        id={installationDrawer?.id || null}
        meta={meta}
        onClose={() => setInstallationDrawer(null)}
        onSaved={() => {
          setInstallationDrawer(null);
          load();
        }}
      />
      <NewVisitModal open={newVisitOpen} meta={meta} onClose={() => setNewVisitOpen(false)} />
      <SettingsModal
        open={settingsOpen}
        settings={meta?.settings}
        onClose={() => setSettingsOpen(false)}
        onSaved={settings => {
          setMeta(prev => ({ ...prev, settings }));
          setSettingsOpen(false);
        }}
      />
      <TemplatesModal
        open={templatesOpen}
        meta={meta}
        onClose={() => setTemplatesOpen(false)}
        onChanged={loadMeta}
      />
    </div>
  );
};

export default Maintenance;
